import {
  ChevronDown,
  DollarSign,
  ShoppingCart,
  TrendingUp,
  BarChart3,
} from "lucide-react";

import { Input } from "shared/components/inputs/Input";
import CardMetrica from "shared/components/cardMetrica/CardMetrica";
import PageContainer from "shared/components/pagecontainer/PageContainer";

import { useMemo, useState } from "react";
import { useVendas } from "shared/hooks/useVendas";

import {
  formatarMoeda,
  reaisParaCentavos,
} from "shared/utils/money";

import styles from "./Relatorios.module.scss";

export const Relatorios = () => {
  const { vendas, loading } = useVendas();

  const [periodo, setPeriodo] = useState("ultimos15");

  const tipoDeMetrica = [
  {
    value: "ultimos30",
    label: "Últimos 30 dias",
  },
  {
    value: "ultimos15",
    label: "Últimos 15 dias",
  },
  {
    value: "Hoje",
    label: "Hoje",
  },
];

  /* ================================
     CONVERSÃO DA DATA
  ================================ */

  const getDate = (data: any) => {
    if (!data) return null;

    if (data.seconds) {
      return new Date(data.seconds * 1000);
    }

    return new Date(data);
  };

  /* ================================
     FILTRAR VENDAS PELO PERÍODO
  ================================ */

  const vendasFiltradas = useMemo(() => {
    const hoje = new Date();

    hoje.setHours(23, 59, 59, 999);

    return vendas.filter((venda) => {
      const dataVenda = getDate(venda.data);

      if (!dataVenda) return false;

      /* HOJE */

      if (periodo === "Hoje") {
        return (
          dataVenda.getDate() === hoje.getDate() &&
          dataVenda.getMonth() === hoje.getMonth() &&
          dataVenda.getFullYear() === hoje.getFullYear()
        );
      }

      /* ÚLTIMOS 15 DIAS */

      if (periodo === "ultimos15") {
        const inicio = new Date(hoje);

        inicio.setDate(hoje.getDate() - 14);
        inicio.setHours(0, 0, 0, 0);

        return (
          dataVenda >= inicio &&
          dataVenda <= hoje
        );
      }

      /* ÚLTIMOS 30 DIAS */

      if (periodo === "ultimos30") {
        const inicio = new Date(hoje);

        inicio.setDate(hoje.getDate() - 29);
        inicio.setHours(0, 0, 0, 0);

        return (
          dataVenda >= inicio &&
          dataVenda <= hoje
        );
      }

      return true;
    });
  }, [vendas, periodo]);

  /* ================================
     RESUMO
  ================================ */

  const resumo = useMemo(() => {
    const faturamento = vendasFiltradas.reduce(
      (acc, venda) =>
        acc + (venda.total || 0),
      0
    );

    const pedidos = vendasFiltradas.length;

    const ticketMedio =
      pedidos > 0
        ? faturamento / pedidos
        : 0;

    return {
      faturamento,
      pedidos,
      ticketMedio,
    };
  }, [vendasFiltradas]);

  /* ================================
     LOADING
  ================================ */

  if (loading) {
    return <p>Carregando...</p>;
  }

  return (
    <PageContainer title="Relatórios" icon={<BarChart3 />} showDesktopSearch={false} showLogo 
    logo={<img src="/imagens/menuLogo.png" alt="Move"/>} contentClassName={styles.contentRelatorios}>

      {/* =================================
          CABEÇALHO DOS RELATÓRIOS
      ================================= */}

      <div className={styles.containerTitulo}>

        <div className={styles.tituloInfo}>
          <span className={styles.tituloLabel}>
            Visão geral
          </span>

          <span className={styles.tituloDescricao}>
            Acompanhe o desempenho das suas vendas
          </span>
        </div>

        {/* =================================
            FILTRO DE PERÍODO
        ================================= */}

        <div className={styles.filtroPeriodo}>

          <div className={styles.filtroLabel}>
            <span>Período</span>
          </div>

          <div className={styles.filtroSelect}>
            <Input
              name="periodo"
              type="select"
              placeholder="Selecione"
              icon={
                <ChevronDown size={16} />
              }
              options={tipoDeMetrica}
              value={periodo}
              onChange={(e: any) =>
                setPeriodo(e.target.value)
              }
              customClass={
                styles.customSelect
              }
            />
          </div>

        </div>

      </div>

      {/* =================================
          MÉTRICAS
      ================================= */}

      <div className={styles.metricas}>

        <CardMetrica
          titulo="Faturamento Total"
          valor={formatarMoeda(
            reaisParaCentavos(
              resumo.faturamento
            )
          )}
          variacao={12.5}
          icon={DollarSign}
          color="green"
        />

        <CardMetrica
          titulo="Total de Pedidos"
          valor={String(
            resumo.pedidos
          )}
          variacao={8.3}
          icon={ShoppingCart}
          color="blue"
        />

        <CardMetrica
          titulo="Ticket Médio"
          valor={formatarMoeda(
            reaisParaCentavos(
              resumo.ticketMedio
            )
          )}
          variacao={-2.1}
          icon={TrendingUp}
          color="purple"
        />

      </div>

    </PageContainer>
  );
};